import { useEffect, useMemo, useState } from 'react'
import { notifications } from '@mantine/notifications'
import type {
  Empresa,
  EmpresaFormData,
  PapelEmpresa,
} from '../../hooks/useEmpresas'
import { BrasilAPIError, buscarCep, buscarCnpj } from '../../lib/brasilapi'
import { formatarCep, formatarCnpj, mensagemErroCnpj } from '../_shared/validators'
import formStyles from '../_shared/CrudForm.module.css'

const PAPEIS_OPTIONS: { value: PapelEmpresa; label: string; tooltip: string }[] = [
  {
    value: 'CLIENTE',
    label: 'Cliente (Contratante)',
    tooltip: 'Marque se esta empresa é dona dos equipamentos e contrata serviços de PMOC.',
  },
  {
    value: 'PLANEJADOR',
    label: 'Planejador (RT projeta o PMOC)',
    tooltip: 'Marque se esta empresa elabora o documento de PMOC com responsável técnico.',
  },
  {
    value: 'EXECUTOR',
    label: 'Executor (manutenção mecânica)',
    tooltip: 'Marque se esta empresa executa manutenção preventiva/corretiva nos equipamentos.',
  },
  {
    value: 'LABORATORIO',
    label: 'Laboratório (análise QAI)',
    tooltip: 'Marque se esta empresa faz análise de qualidade do ar interior.',
  },
]

interface Props {
  initial?: Empresa
  papelDefault?: PapelEmpresa
  onSubmit: (data: EmpresaFormData) => void
  isLoading?: boolean
  submitLabel?: string
}

const EMPTY: EmpresaFormData = {
  razao_social: '',
  nome_fantasia: '',
  cnpj: '',
  inscricao_estadual: '',
  endereco_logradouro: '',
  endereco_numero: '',
  endereco_complemento: '',
  endereco_bairro: '',
  endereco_cidade: '',
  endereco_uf: '',
  endereco_cep: '',
  telefone: '',
  email: '',
  website: '',
  registro_pj_conselho: '',
  registro_pj_conselho_outro: '',
  registro_pj_numero: '',
  registro_pj_uf: '',
  papeis: [],
  grupo_economico: null,
}

export default function EmpresaForm({
  initial,
  papelDefault,
  onSubmit,
  isLoading,
  submitLabel = 'Salvar',
}: Props) {
  const [form, setForm] = useState<EmpresaFormData>(() => {
    if (initial) {
      return {
        razao_social: initial.razao_social,
        nome_fantasia: initial.nome_fantasia,
        cnpj: initial.cnpj,
        inscricao_estadual: initial.inscricao_estadual,
        endereco_logradouro: initial.endereco_logradouro,
        endereco_numero: initial.endereco_numero,
        endereco_complemento: initial.endereco_complemento,
        endereco_bairro: initial.endereco_bairro,
        endereco_cidade: initial.endereco_cidade,
        endereco_uf: initial.endereco_uf,
        endereco_cep: initial.endereco_cep,
        telefone: initial.telefone,
        email: initial.email,
        website: initial.website,
        registro_pj_conselho: initial.registro_pj_conselho,
        registro_pj_conselho_outro: initial.registro_pj_conselho_outro,
        registro_pj_numero: initial.registro_pj_numero,
        registro_pj_uf: initial.registro_pj_uf,
        papeis: initial.papeis,
        grupo_economico: initial.grupo_economico,
      }
    }
    return { ...EMPTY, papeis: papelDefault ? [papelDefault] : [] }
  })

  const [erros, setErros] = useState<Record<string, string>>({})
  const [loadingCnpj, setLoadingCnpj] = useState(false)
  const [loadingCep, setLoadingCep] = useState(false)

  const precisaConselho = useMemo(
    () =>
      form.papeis.includes('PLANEJADOR') ||
      form.papeis.includes('EXECUTOR') ||
      form.papeis.includes('LABORATORIO'),
    [form.papeis],
  )

  // Limpa erro do campo quando começa a editar.
  // Exceção: `cnpj` tem validação semântica (DV) — não limpa só porque campo
  // tem valor; isso acontece manualmente no blur (handleCnpjBlur) ou submit.
  useEffect(() => {
    if (Object.keys(erros).length === 0) return
    const novosErros = { ...erros }
    let mudou = false
    for (const k of Object.keys(novosErros)) {
      if (k === 'cnpj') continue
      if ((form as unknown as Record<string, unknown>)[k]) {
        delete novosErros[k]
        mudou = true
      }
    }
    if (mudou) setErros(novosErros)
  }, [form, erros])

  function togglePapel(p: PapelEmpresa) {
    setForm((f) => {
      const set = new Set(f.papeis)
      if (set.has(p)) set.delete(p)
      else set.add(p)
      return { ...f, papeis: Array.from(set) }
    })
  }

  function validar(): boolean {
    const novos: Record<string, string> = {}
    if (!form.razao_social.trim()) novos.razao_social = 'Obrigatório.'
    if (form.papeis.length === 0)
      novos.papeis = 'Selecione pelo menos um papel.'
    const erroCnpj = mensagemErroCnpj(form.cnpj)
    if (erroCnpj) novos.cnpj = erroCnpj
    setErros(novos)
    return Object.keys(novos).length === 0
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validar()) return
    onSubmit(form)
  }

  function update<K extends keyof EmpresaFormData>(k: K, v: EmpresaFormData[K]) {
    setForm((f) => ({ ...f, [k]: v }))
  }

  async function handleCnpjBlur() {
    // Campo vazio: limpa erro anterior, não dispara nada.
    if (!form.cnpj.trim()) {
      setErros((prev) => {
        if (!prev.cnpj) return prev
        const { cnpj: _ignored, ...rest } = prev
        return rest
      })
      return
    }
    const erro = mensagemErroCnpj(form.cnpj)
    if (erro) {
      setErros((prev) => ({ ...prev, cnpj: erro }))
      notifications.show({ color: 'yellow', message: erro })
      return
    }
    // CNPJ válido — limpa erro anterior antes de buscar.
    setErros((prev) => {
      if (!prev.cnpj) return prev
      const { cnpj: _ignored, ...rest } = prev
      return rest
    })
    setLoadingCnpj(true)
    try {
      const dados = await buscarCnpj(form.cnpj)
      setForm((f) => {
        const fill = (cur: string, novo: string | undefined) =>
          cur.trim() === '' && novo ? novo : cur
        return {
          ...f,
          razao_social: fill(f.razao_social, dados.razao_social),
          nome_fantasia: fill(f.nome_fantasia, dados.nome_fantasia),
          endereco_logradouro: fill(f.endereco_logradouro, dados.logradouro),
          endereco_numero: fill(f.endereco_numero, dados.numero),
          endereco_complemento: fill(f.endereco_complemento, dados.complemento),
          endereco_bairro: fill(f.endereco_bairro, dados.bairro),
          endereco_cidade: fill(f.endereco_cidade, dados.municipio),
          endereco_uf: fill(f.endereco_uf, dados.uf?.toUpperCase()),
          endereco_cep: fill(f.endereco_cep, dados.cep),
          telefone: fill(f.telefone, dados.telefone),
          email: fill(f.email, dados.email),
        }
      })
      notifications.show({
        color: 'green',
        message:
          'CNPJ encontrado — campos preenchidos automaticamente. Confira antes de salvar.',
      })
    } catch (err) {
      if (err instanceof BrasilAPIError) {
        notifications.show({
          color: err.kind === 'not_found' ? 'yellow' : 'orange',
          message: err.message,
        })
      } else {
        throw err
      }
    } finally {
      setLoadingCnpj(false)
    }
  }

  async function handleCepBlur() {
    const digitos = form.endereco_cep.replace(/\D/g, '')
    if (digitos.length !== 8) return
    setLoadingCep(true)
    try {
      const dados = await buscarCep(form.endereco_cep)
      setForm((f) => {
        const fill = (cur: string, novo: string | undefined) =>
          cur.trim() === '' && novo ? novo : cur
        return {
          ...f,
          endereco_logradouro: fill(f.endereco_logradouro, dados.street),
          endereco_bairro: fill(f.endereco_bairro, dados.neighborhood),
          endereco_cidade: fill(f.endereco_cidade, dados.city),
          endereco_uf: fill(f.endereco_uf, dados.state?.toUpperCase()),
        }
      })
    } catch (err) {
      if (err instanceof BrasilAPIError) {
        notifications.show({
          color: err.kind === 'not_found' ? 'yellow' : 'orange',
          message: err.message,
        })
      } else {
        throw err
      }
    } finally {
      setLoadingCep(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className={formStyles.form}>
      <section className={formStyles.section}>
        <h2 className={formStyles.sectionTitle}>Identificação</h2>
        <div className={formStyles.grid2}>
          <div className={`${formStyles.field} ${formStyles.span2}`}>
            <label htmlFor="razao_social">Razão Social *</label>
            <input
              id="razao_social"
              data-testid="empresa-razao-social"
              value={form.razao_social}
              onChange={(e) => update('razao_social', e.target.value)}
              required
            />
            {erros.razao_social && (
              <small style={{ color: '#C62828' }}>{erros.razao_social}</small>
            )}
          </div>

          <div className={formStyles.field}>
            <label htmlFor="nome_fantasia">Nome Fantasia</label>
            <input
              id="nome_fantasia"
              data-testid="empresa-nome-fantasia"
              value={form.nome_fantasia}
              onChange={(e) => update('nome_fantasia', e.target.value)}
            />
          </div>

          <div className={formStyles.field}>
            <label htmlFor="cnpj">CNPJ</label>
            <input
              id="cnpj"
              data-testid="empresa-cnpj"
              placeholder="00.000.000/0000-00"
              value={form.cnpj}
              onChange={(e) => update('cnpj', formatarCnpj(e.target.value))}
              onBlur={handleCnpjBlur}
              disabled={loadingCnpj}
              inputMode="numeric"
            />
            {loadingCnpj && (
              <small style={{ color: '#666' }}>Buscando dados na BrasilAPI…</small>
            )}
            {erros.cnpj && (
              <small style={{ color: '#C62828' }}>{erros.cnpj}</small>
            )}
          </div>

          <div className={formStyles.field}>
            <label htmlFor="inscricao_estadual">Inscrição Estadual</label>
            <input
              id="inscricao_estadual"
              value={form.inscricao_estadual}
              onChange={(e) => update('inscricao_estadual', e.target.value)}
            />
          </div>
        </div>
      </section>

      <section className={formStyles.section}>
        <h2 className={formStyles.sectionTitle}>Papéis exercidos *</h2>
        <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: 12 }}>
          Marque os papéis que esta empresa exerce. Uma empresa pode ter mais
          de um papel (ex: cliente que também faz a manutenção).
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {PAPEIS_OPTIONS.map((p) => (
            <label
              key={p.value}
              title={p.tooltip}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                data-testid={`empresa-papel-${p.value.toLowerCase()}`}
                checked={form.papeis.includes(p.value)}
                onChange={() => togglePapel(p.value)}
              />
              <span>{p.label}</span>
            </label>
          ))}
        </div>
        {erros.papeis && (
          <small style={{ color: '#C62828' }}>{erros.papeis}</small>
        )}
      </section>

      <section className={formStyles.section}>
        <h2 className={formStyles.sectionTitle}>Endereço</h2>
        <div className={formStyles.grid2}>
          <div className={`${formStyles.field} ${formStyles.span2}`}>
            <label htmlFor="endereco_logradouro">Logradouro</label>
            <input
              id="endereco_logradouro"
              value={form.endereco_logradouro}
              onChange={(e) => update('endereco_logradouro', e.target.value)}
            />
          </div>

          <div className={formStyles.field}>
            <label htmlFor="endereco_numero">Número</label>
            <input
              id="endereco_numero"
              value={form.endereco_numero}
              onChange={(e) => update('endereco_numero', e.target.value)}
            />
          </div>

          <div className={formStyles.field}>
            <label htmlFor="endereco_complemento">Complemento</label>
            <input
              id="endereco_complemento"
              value={form.endereco_complemento}
              onChange={(e) => update('endereco_complemento', e.target.value)}
            />
          </div>

          <div className={formStyles.field}>
            <label htmlFor="endereco_bairro">Bairro</label>
            <input
              id="endereco_bairro"
              value={form.endereco_bairro}
              onChange={(e) => update('endereco_bairro', e.target.value)}
            />
          </div>

          <div className={formStyles.field}>
            <label htmlFor="endereco_cidade">Cidade</label>
            <input
              id="endereco_cidade"
              value={form.endereco_cidade}
              onChange={(e) => update('endereco_cidade', e.target.value)}
            />
          </div>

          <div className={formStyles.field}>
            <label htmlFor="endereco_uf">UF</label>
            <input
              id="endereco_uf"
              maxLength={2}
              value={form.endereco_uf}
              onChange={(e) => update('endereco_uf', e.target.value.toUpperCase())}
            />
          </div>

          <div className={formStyles.field}>
            <label htmlFor="endereco_cep">CEP</label>
            <input
              id="endereco_cep"
              data-testid="empresa-cep"
              placeholder="00000-000"
              value={form.endereco_cep}
              onChange={(e) => update('endereco_cep', formatarCep(e.target.value))}
              onBlur={handleCepBlur}
              disabled={loadingCep}
              inputMode="numeric"
            />
            {loadingCep && (
              <small style={{ color: '#666' }}>Buscando dados do CEP…</small>
            )}
          </div>
        </div>
      </section>

      <section className={formStyles.section}>
        <h2 className={formStyles.sectionTitle}>Contato</h2>
        <div className={formStyles.grid2}>
          <div className={formStyles.field}>
            <label htmlFor="telefone">Telefone</label>
            <input
              id="telefone"
              value={form.telefone}
              onChange={(e) => update('telefone', e.target.value)}
            />
          </div>

          <div className={formStyles.field}>
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
            />
          </div>

          <div className={`${formStyles.field} ${formStyles.span2}`}>
            <label htmlFor="website">Website</label>
            <input
              id="website"
              type="url"
              placeholder="https://..."
              value={form.website}
              onChange={(e) => update('website', e.target.value)}
            />
          </div>
        </div>
      </section>

      {precisaConselho && (
        <section className={formStyles.section}>
          <h2 className={formStyles.sectionTitle}>
            Registro PJ no Conselho Profissional
          </h2>
          <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: 12 }}>
            Opcional no cadastro. Informe quando a empresa possuir registro PJ.
            O responsável técnico e seu conselho são cadastrados separadamente.
          </p>
          <div className={formStyles.grid2}>
            <div className={formStyles.field}>
              <label htmlFor="registro_pj_conselho">Conselho</label>
              <select
                id="registro_pj_conselho"
                value={form.registro_pj_conselho}
                onChange={(e) => update('registro_pj_conselho', e.target.value)}
              >
                <option value="">— selecione —</option>
                <option value="CREA">CREA</option>
                <option value="CRQ">CRQ</option>
                <option value="CRBM">CRBM</option>
                <option value="CRECN">CRECN</option>
                <option value="CFTA">CFTA</option>
                <option value="OUTRO">Outro</option>
              </select>
              {erros.registro_pj_conselho && (
                <small style={{ color: '#C62828' }}>
                  {erros.registro_pj_conselho}
                </small>
              )}
            </div>

            {form.registro_pj_conselho === 'OUTRO' && (
              <div className={formStyles.field}>
                <label htmlFor="registro_pj_conselho_outro">
                  Descrição do conselho
                </label>
                <input
                  id="registro_pj_conselho_outro"
                  value={form.registro_pj_conselho_outro}
                  onChange={(e) =>
                    update('registro_pj_conselho_outro', e.target.value)
                  }
                />
              </div>
            )}

            <div className={formStyles.field}>
              <label htmlFor="registro_pj_numero">Número do registro</label>
              <input
                id="registro_pj_numero"
                value={form.registro_pj_numero}
                onChange={(e) => update('registro_pj_numero', e.target.value)}
              />
            </div>

            <div className={formStyles.field}>
              <label htmlFor="registro_pj_uf">UF do registro</label>
              <input
                id="registro_pj_uf"
                maxLength={2}
                value={form.registro_pj_uf}
                onChange={(e) =>
                  update('registro_pj_uf', e.target.value.toUpperCase())
                }
              />
            </div>
          </div>
        </section>
      )}

      <div className={formStyles.actions}>
        <button
          type="submit"
          className={formStyles.btnPrimary}
          disabled={isLoading}
          data-testid="empresa-submit"
        >
          {isLoading ? 'Salvando...' : submitLabel}
        </button>
      </div>
    </form>
  )
}
